import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { HubPageGrid } from "@/components/content/HubPageGrid";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Badge } from "@/components/ui/badge";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const fixPages = [
  {
    title: "Lawn Care Website Not Getting Leads",
    description: "Your lawn care website gets traffic but no calls? Here's how to diagnose and fix the problem.",
    href: "/website-fixes/lawn-care-website-not-getting-leads/"
  },
  {
    title: "Landscaping Website Not Getting Leads",
    description: "Find out why your landscaping website isn't converting visitors into quote requests.",
    href: "/website-fixes/landscaping-website-not-getting-leads/"
  },
  {
    title: "Lawn Care Website Slow on Mobile",
    description: "A slow mobile site kills conversions. Learn how to speed up your lawn care website.",
    href: "/website-fixes/lawn-care-website-slow-mobile/"
  },
  {
    title: "Landscaping Website Speed Optimization",
    description: "Fix your slow landscaping website with these proven speed optimization techniques.",
    href: "/website-fixes/landscaping-website-slow/"
  },
  {
    title: "Quote Form Not Submitting",
    description: "Is your lawn care quote form broken? Troubleshoot common form submission issues.",
    href: "/website-fixes/quote-form-not-submitting/"
  },
  {
    title: "Landscaping Quote Form Low Conversion",
    description: "Your form works but nobody fills it out? Optimize your landscaping quote form for more leads.",
    href: "/website-fixes/landscaping-quote-form-conversion/"
  },
  {
    title: "Service Pages Not Ranking",
    description: "Your service pages aren't showing up on Google? Here's how to fix your SEO structure.",
    href: "/website-fixes/service-pages-not-ranking/"
  },
  {
    title: "Service Area Pages Not Ranking",
    description: "Local SEO not working? Learn why your service area pages aren't ranking and how to fix it.",
    href: "/website-fixes/service-area-pages-not-ranking/"
  },
  {
    title: "Not Getting Calls from Google Maps",
    description: "Your Google Business Profile isn't driving calls? Diagnose and fix your GBP issues.",
    href: "/website-fixes/no-calls-from-google-maps/"
  },
  {
    title: "High Traffic But No Calls",
    description: "Getting website visitors but no phone calls? Here's what's wrong and how to fix it.",
    href: "/website-fixes/high-traffic-no-calls/"
  },
  {
    title: "How to Add a Sticky Call Button",
    description: "Add a sticky call button to your website to make it easy for mobile visitors to call you.",
    href: "/website-fixes/sticky-call-button/"
  },
  {
    title: "How to Add Google Reviews to Your Website",
    description: "Display Google reviews on your lawn care website to build trust and boost conversions.",
    href: "/website-fixes/add-reviews-to-website/"
  }
];

const faqs = [
  {
    question: "Why isn't my lawn care website getting leads?",
    answer: "Common reasons include: no clear call-to-action, slow loading speed, confusing navigation, no trust signals (reviews/photos), missing service area information, or poor mobile experience. Our fix guides help you diagnose the specific issue."
  },
  {
    question: "How do I know if my website speed is a problem?",
    answer: "If your site takes more than 3 seconds to load on mobile, you're losing leads. Use Google PageSpeed Insights to check your score. Aim for 50+ on mobile, but 70+ is ideal."
  },
  {
    question: "Why isn't my Google Business Profile getting calls?",
    answer: "Common issues include incomplete profile, wrong categories, no reviews, outdated photos, or missing service areas. GBP optimization is critical for local lawn care businesses."
  },
  {
    question: "Should I fix my current website or get a new one?",
    answer: "It depends on the issues. Minor fixes (speed, forms, CTAs) can often be done on existing sites. But if your site has fundamental problems (old design, poor structure, no mobile optimization), a new website may be more cost-effective."
  },
  {
    question: "Why do my service pages not rank on Google?",
    answer: "Usually it's due to thin content, missing keywords, no internal links, duplicate content, or poor site structure. Each service should have its own optimized page with unique, helpful content."
  },
  {
    question: "How do I add trust signals to my website?",
    answer: "Display Google reviews, before/after photos, awards/certifications, years in business, and customer testimonials. These build confidence and increase conversions."
  },
  {
    question: "What's a good conversion rate for lawn care websites?",
    answer: "Most lawn care websites convert 2-5% of visitors into leads. Top-performing sites can achieve 5-10%. If you're below 2%, there's significant room for improvement."
  },
  {
    question: "Can these fixes work for landscaping websites too?",
    answer: "Yes! Most website issues are similar for lawn care and landscaping businesses. We have specific guides for both niches where relevant."
  },
  {
    question: "How long does it take to see results after fixing issues?",
    answer: "Speed and conversion fixes show immediate results. SEO improvements typically take 2-4 weeks for Google to re-crawl and rank your pages. Major ranking changes may take 1-3 months."
  },
  {
    question: "Should I try to fix these issues myself or hire someone?",
    answer: "Simple fixes like adding a phone number or updating content are DIY-friendly. Technical issues like site speed, SEO structure, or form problems often benefit from professional help."
  }
];

const WebsiteFixesHub = () => {
  const pageUrl = "https://www.leafrushmarketing.com/website-fixes/";
  
  return (
    <>
      <SEO
        title="Fix Lawn Care Website Not Getting Leads | Leaf Rush"
        description="Your lawn care website isn't getting leads? Diagnose and fix common website problems with our step-by-step guides. Speed, forms, SEO, and more."
        canonical={pageUrl}
        ogUrl={pageUrl}
        keywords="fix lawn care website, lawn care website not getting leads, landscaping website problems"
      />
      <PageSchema
        pageUrl={pageUrl}
        pageName="Fix Lawn Care Website Not Getting Leads"
        pageDescription="Diagnose and fix common lawn care and landscaping website problems. Step-by-step guides for speed, forms, SEO, and conversion issues."
        faqs={faqs}
      />

      <TopCTA />

      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge variant="secondary">Problem/Solution Guides</Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Fix Your Lawn Care Website{" "}
              <span className="text-primary">Not Getting Leads</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Something's wrong with your website but you're not sure what. 
              These guides help you diagnose problems and fix them step-by-step.
            </p>
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* Start Here Section */}
      <section className="py-12 bg-accent/20">
        <div className="container px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">Want a Fresh Start Instead?</h2>
            <p className="text-center text-muted-foreground mb-8">
              Sometimes fixing an old website costs more than building a new one. 
              Check out our done-for-you website options:
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <NavLink to="/websites/">
                <Button size="lg" className="gap-2">
                  Website Pricing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </NavLink>
              <NavLink to="/lawn-care-website-design/">
                <Button size="lg" variant="outline">
                  Lawn Care Websites
                </Button>
              </NavLink>
              <NavLink to="/landscaping-website-design/">
                <Button size="lg" variant="outline">
                  Landscaping Websites
                </Button>
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* Grid of Fix Pages */}
      <section className="py-16">
        <div className="container px-4 md:px-6">
          <h2 className="text-3xl font-bold mb-8 text-center">All Website Fix Guides</h2>
          <HubPageGrid items={fixPages} />
        </div>
      </section>

      <MidPageCTA />

      {/* Internal Links */}
      <section className="py-12">
        <div className="container px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <InternalLinks
              title="Related Resources"
              links={[
                { to: "/comparisons/", label: "Website Builder Comparisons" },
                { to: "/website-templates/", label: "Templates & Checklists" },
                { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
                { to: "/service-area-pages-local-seo/", label: "Service Area Pages for Local SEO" },
                { to: "/growth-add-ons/google-ads/", label: "Google Ads Management" },
                { to: "/growth-add-ons/gbp/", label: "Google Business Profile Optimization" },
                { to: "/resources/", label: "All Resources" },
                { to: "/contact/", label: "Contact Us" }
              ]}
            />
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />

      <BottomCTA />

      <CalendlyEmbed />
    </>
  );
};

export default WebsiteFixesHub;
